'use strict';

import usePhoneMask, { resolvePhoneInput } from '../hooks/usePhoneMask';

export default function PhoneInput ( {
    label = "",
    value: userValue,
    phoneCode,
    error = null,
    disabled = false,
    autoFocus = false,
    readOnly = false,
    className,
    inputClassName = "",
    placeholder = "",
    onChange,
    onCompleteChange,
    onCodeChange,
    showFlagSelect = true,
} )
{
    const inputRef = React.useRef ();

    const [ id ] = React.useState ( () => `FormInput_phone_${ _.uniqueId () }` );

    // Единственный источник правды — value-проп. Хук только выводит
    // display/страну/маску; внутреннего текстового стейта нет.
    const { digits, cleared, display, country, isComplete } = usePhoneMask ( {
        value: userValue,
        phoneCode,
    } );
    const flagCode = React.useMemo ( () => country ? country.code.toLowerCase () : 'gj', [ country ] );

    // Колбэки родителей часто инлайновые — держим свежие в рефах,
    // чтобы эффекты не пересоздавались и не ловили устаревшие замыкания.
    const onChangeRef = React.useRef ( onChange );
    onChangeRef.current = onChange;
    const onCodeChangeRef = React.useRef ( onCodeChange );
    onCodeChangeRef.current = onCodeChange;
    const onCompleteChangeRef = React.useRef ( onCompleteChange );
    onCompleteChangeRef.current = onCompleteChange;

    // Префилл страны при пустом значении (маунт, дожавшаяся геолокация).
    // Делаем его только один раз: как только в поле появились цифры или
    // префилл уже случился, автоподстановка больше не повторяется. Иначе
    // после ручной очистки поле снова заполнялось кодом страны, а у стран
    // с многозначным кодом промежуточные состояния ("+38", "+3") ещё и
    // сбрасывали запомненный код страны.
    const didPrefillRef = React.useRef ( false );
    React.useEffect ( () =>
    {
        if ( digits )
        {
            didPrefillRef.current = true;
            return;
        }

        if ( !didPrefillRef.current && country )
        {
            didPrefillRef.current = true;
            onChangeRef.current && onChangeRef.current ( country.phone );
        }
    }, [ digits, country ] );

    // Уведомляем о смене резолвленной страны (детект вводом, phoneCode,
    // сохранённая, гео). Дубли не шлём и не сбрасываем код на промежуточных
    // состояниях, когда цифры есть, но страна ещё не распознана.
    const prevCodeRef = React.useRef ();
    React.useEffect ( () =>
    {
        if ( !country ) return;

        const code = country.code;
        if ( prevCodeRef.current !== code )
        {
            prevCodeRef.current = code;
            onCodeChangeRef.current && onCodeChangeRef.current ( code );
        }
    }, [ country ] );

    React.useEffect ( () =>
    {
        onCompleteChangeRef.current && onCompleteChangeRef.current ( isComplete );
    }, [ isComplete ] );

    // Ввод пользователя: нормализация (всегда ведущий "+", цифры обрезаются
    // по ёмкости маски страны) — и один вызов наверх. Внешнее значение при
    // этом не трогаем: циклов «sync <-> echo» больше нет по построению.
    const handleInput = React.useCallback ( ( e ) =>
    {
        const { cleared } = resolvePhoneInput ( e.target.value );
        onChangeRef.current && onChangeRef.current ( cleared );
    }, [] );

    const handleFlagClick = () =>
    {
        $.fancybox.open ( {
            src: '#reactFlagSelectModal',
            props: {
                onSelect: country =>
                {
                    handleCountrySelect ( country );
                    inputRef.current.focus ();
                },
            }
        } );
    };

    // Ручная смена страны: в значение — dial-префикс,
    // дальше ввод снова детектит страну по цифрам.
    const handleCountrySelect = ( selected ) =>
    {
        if ( !selected ) return;
        try {
            if ( typeof storageSet === 'function' ) storageSet ( 'phoneCountryCode', selected.code );
        } catch ( e ) {}
        onChangeRef.current && onChangeRef.current ( selected.phone );
        onCodeChangeRef.current && onCodeChangeRef.current ( selected.code );
    };

    return (
        <div className={ clsx ( 'inputbox', className, 'phone-inputbox', {
            changed: cleared,
            'error-filed': error
        } ) }>
            {label && <label htmlFor={ id }>{ label }</label>}
            <div className="phone-code__wrap">
                {showFlagSelect && <div className="flag-select" onClick={readOnly ? null : handleFlagClick}>
                    <img src={`${themeDir}/images/flags/${flagCode}.svg`} alt=""/>
                </div>}
                <input
                    id={ id }
                    ref={ inputRef }
                    type="text"
                    value={ display }
                    readOnly={ readOnly }
                    disabled={ disabled }
                    autoComplete={ 'off' }
                    inputMode="tel"
                    placeholder={ placeholder }
                    onInput={ handleInput }
                    autoFocus={ autoFocus }
                    className={inputClassName}
                />
            </div>

            { !!error && <div className="error-message">
                { error }
            </div> }
        </div>
    );
}
